import { inject, injectable } from 'inversify'
import { cloneDeep, upperFirst } from 'lodash-es'
import { SERVICE_TYPES } from '@/core/container/types'
import type { PaginationUrlFilterSingleType, PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import TmLogicError from '@/core/error/tmLogicError'
import { dateFilterValueVariant, type DateFilterValueType } from '@/components/shared/tables/topFilter/dateFilter/types'
import type DateTimeService from '@/services/dateTimeService'
import {
  DATE_FORMATES,
  type DateFormatesType,
  type FilterToFormattedTextConvertable,
  type FormattedTextNode,
  DateTimeRanges,
} from '@/services/types'
import type UserService from '@/services/domain/user/userService'
import type TranslateService from '@/services/translateService'
import type { RelativeDateTimeService } from '@/services/datetime/relativeDateTimeService'
import {
  isTRelativeDateTimeRange,
  type TRelativeDateTimeRange,
  type TRelativeDateTime,
} from '@/services/datetime/types'
import type { Optional } from '@/types'
import { mapFilterToRange } from '@/services/filters/mapFilterToRange'

const divider = ' - '

@injectable()
export default class DateFilterMapper implements FilterToFormattedTextConvertable {
  constructor(
    @inject(SERVICE_TYPES.DateTimeService) protected readonly dateTimeService: DateTimeService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.RelativeDateTimeService) protected readonly relativeDateTimeService: RelativeDateTimeService,
  ) {}

  public mapDateFilter(_filter: PaginationUrlFilterSingleType, inputFilterName: string, outputFilterName: string) {
    const filter = cloneDeep(_filter)
    const type = (filter[inputFilterName] as any).eq

    if (!dateFilterValueVariant.includes(type)) {
      throw new TmLogicError('incorrect date filter type')
    }

    const newDate = () => {
      const startDate = new Date()
      return new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()))
    }

    const getFilterValue = (count: number) => {
      return `-${count} days midnight`
    }

    const formatDate = (_date: Date) => {
      const currentUser = this.userService.currentUser(['timezone'])
      const date = this.dateTimeService.zonedTimeToUtc(_date, currentUser.timezone.timezone)
      const force24format = currentUser.displayTimeFormat === '24h'
      const normalizedDate = this.dateTimeService.toDate(date)

      return this.dateTimeService.formatDateForTimezone(
        normalizedDate,
        DATE_FORMATES.ISO_FORMAT_WITH_TIMEZONE,
        undefined,
        false,
        force24format,
        false,
      )
    }

    const handlers: Record<Exclude<DateFilterValueType, 'All'>, () => void> = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Last 30 days': () => {
        filter[outputFilterName] = {
          gte: getFilterValue(29),
        }
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Last 60 days': () => {
        filter[outputFilterName] = {
          gte: getFilterValue(59),
        }
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Last week': () => {
        const currentWeekDate = newDate()
        const prevWeekDate = this.dateTimeService.setDate(currentWeekDate, currentWeekDate.getDate() - 7)

        const firstDateCurrentWeek = this.dateTimeService.startOfWeek(currentWeekDate)
        const firstDatePrevWeek = this.dateTimeService.startOfWeek(prevWeekDate)

        filter[outputFilterName] = {
          gte: formatDate(firstDatePrevWeek),
          lte: formatDate(firstDateCurrentWeek),
        }
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Pick date range': () => {
        const from = (filter.from as any)?.eq
        if (typeof from !== 'string') {
          throw new TmLogicError('"from" should be type string')
        }
        const to = (filter.to as any)?.eq
        if (typeof to !== 'string') {
          throw new TmLogicError('"to" should be type string')
        }
        filter[outputFilterName] = {
          gte: formatDate(this.dateTimeService.startOfDay(new Date(from))),
          lte: formatDate(this.dateTimeService.endOfDay(new Date(to))),
        }
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Previous month': () => {
        const lastDatePrevMonth = this.dateTimeService.setDate(newDate(), 0)
        const firstDatePrevMonth = this.dateTimeService.setDate(lastDatePrevMonth, 1)
        filter[outputFilterName] = {
          gte: formatDate(firstDatePrevMonth),
          lte: formatDate(lastDatePrevMonth),
        }
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'This month': () => {
        filter[outputFilterName] = {
          gte: formatDate(this.dateTimeService.setDate(newDate(), 1)),
        }
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'This week': () => {
        filter[outputFilterName] = {
          gte: formatDate(this.dateTimeService.setDay(newDate(), 1)),
        }
      },
      Today: () => {
        filter[outputFilterName] = {
          gte: 'today midnight',
          lt: 'tomorrow midnight',
        }
      },
      Yesterday: () => {
        filter[outputFilterName] = {
          gte: 'yesterday midnight',
          lt: 'today midnight',
        }
      },
    }
    handlers[type]()
    delete filter[inputFilterName]
    delete filter.from
    delete filter.to
    return filter
  }

  public getLabel(
    value: Optional<TRelativeDateTimeRange>,
    dateFormat: DateFormatesType = DATE_FORMATES.DATE_FORMAT_YEAR,
  ): { main: string; op?: string } {
    switch (true) {
      case this.relativeDateTimeService.isRelativeDateForToday(value):
        return {
          main: this.translateService.t(`units.datetime.ranges.${DateTimeRanges.THIS_DAY}`),
        }

      case this.relativeDateTimeService.isRelativeDateForYesterday(value):
        return {
          main: this.translateService.t(`units.datetime.ranges.${DateTimeRanges.LAST_DAY}`),
        }

      case this.relativeDateTimeService.isRelativeDateForLast7Days(value):
        return {
          main: this.translateService.t(`units.datetime.ranges.${DateTimeRanges.LAST_7_DAYS}`),
        }

      case this.relativeDateTimeService.isRelativeDateForLast30Days(value):
        return {
          main: this.translateService.t(`units.datetime.ranges.${DateTimeRanges.LAST_30_DAYS}`),
        }

      case this.relativeDateTimeService.isRelativeDateForEver(value):
        return {
          main: this.translateService.t(`units.datetime.ranges.${DateTimeRanges.EVER}`),
        }

      case this.relativeDateTimeService.isRelativeDateForNoDate(value):
        return {
          main: this.translateService.t(`units.datetime.ranges.${DateTimeRanges.NEVER}`),
        }

      case this.relativeDateTimeService.isRelativeDateForLastPeriod(value): {
        const r = this.relativeDateTimeService.getPeriodFormRelativeDateForLastPeriod(value)

        return {
          main: this.translateService.tc(`units.datetime.period.${r.period}`, r.count),
          op: this.translateService.t('filters.datetime.rangeLast'),
        }
      }

      case this.relativeDateTimeService.isRelativeDateForNotLastPeriod(value): {
        const r = this.relativeDateTimeService.getPeriodFormRelativeDateForNotLastPeriod(value)

        return {
          main: this.translateService.tc(`units.datetime.period.${r.period}`, r.count),
          op: this.translateService.t('filters.datetime.rangeNotLast'),
        }
      }

      case isTRelativeDateTimeRange(value): {
        const dateFrom = this.relativeDateTimeService.isValidDate(value?.start)
          ? this.utcToUserTimezone(this.dateTimeService.toDate(value.start))
          : null
        const dateStringFrom = dateFrom ? this.dateTimeService.format(dateFrom, dateFormat, false) : undefined

        const dateTo = this.relativeDateTimeService.isValidDate(value?.end)
          ? this.utcToUserTimezone(this.dateTimeService.toDate(value.end))
          : null
        const dateStringTo = dateTo ? this.dateTimeService.format(dateTo, dateFormat, false) : undefined

        if (dateFrom && dateTo) {
          if (this.dateTimeService.isSameDay(dateFrom, dateTo)) {
            return {
              main: `${dateStringFrom}`,
            }
          }

          return {
            main: `${dateStringFrom}${divider}${dateStringTo}`,
          }
        }

        if (dateFrom) {
          return {
            main: `${dateStringFrom}`,
            op: this.translateService.t('filters.datetime.rangeFrom'),
          }
        }

        return {
          main: `${dateStringTo}`,
          op: this.translateService.t('filters.datetime.rangeTo'),
        }
      }

      default:
        return {
          main: this.translateService.t('common.all'),
        }
    }
  }

  public toFormattedText(filterValue: PaginationUrlFilterScalarValueType): FormattedTextNode[] {
    const relativeDateTimeRange = mapFilterToRange<TRelativeDateTime>(filterValue)
    if (!relativeDateTimeRange) return []

    const { main, op } = this.getLabel(relativeDateTimeRange)
    return [
      {
        type: 'badge',
        value: op ? `${upperFirst(op)} ${main}` : main,
      },
    ]
  }

  protected utcToUserTimezone(date: Date): Date {
    const { timezone } = this.userService.currentUser(['timezone'])
    return this.dateTimeService.utcToZonedTime(
      date ? new Date(date).toISOString() : new Date().toISOString(),
      timezone.timezone,
    )
  }
}
